import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Image as ImageIcon, Truck, Film, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { priceLabel, hours } from '../lib/packageData';

// Side-by-side comparison table for a service's packages within a category.
const ROWS = {
  Photography: [
    { key: 'price', label: 'Price', icon: DollarSign },
    { key: 'coverage', label: 'Coverage', icon: Clock },
    { key: 'images', label: 'Edited images', icon: ImageIcon },
    { key: 'delivery', label: 'Delivery', icon: Truck },
    { key: 'scope', label: 'Scope', icon: null },
  ],
  Videography: [
    { key: 'price', label: 'Price', icon: DollarSign },
    { key: 'coverage', label: 'On-site time', icon: Clock },
    { key: 'films', label: 'Finished films', icon: Film },
    { key: 'delivery', label: 'Delivery', icon: Truck },
    { key: 'scope', label: 'Scope', icon: null },
  ],
};

const cellValue = (pkg, key) => {
  switch (key) {
    case 'price':
      return <span className="text-[#C5A880] font-bold text-base">{priceLabel(pkg)}</span>;
    case 'coverage':
      if (!pkg.minutes) return <span className="text-[#EADBC8]/40">By arrangement</span>;
      return pkg.service === 'Videography' ? `Up to ${hours(pkg.minutes)}` : `${hours(pkg.minutes)}`;
    case 'images':
      if (pkg.finished_images == null) return <span className="text-[#EADBC8]/40">—</span>;
      return typeof pkg.finished_images === 'number' ? `${pkg.finished_images} images` : pkg.finished_images;
    case 'films':
      return pkg.films || <span className="text-[#EADBC8]/40">—</span>;
    case 'delivery':
      return pkg.delivery || <span className="text-[#EADBC8]/40">By arrangement</span>;
    case 'scope':
      return pkg.scope ? <span className="text-[#EADBC8]/60 text-xs leading-relaxed">{pkg.scope}</span> : <span className="text-[#EADBC8]/40">—</span>;
    default:
      return null;
  }
};

export const PackageCompare = ({ service, packages }) => {
  const rows = ROWS[service] || ROWS.Photography;
  if (!packages.length) return null;

  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:thin]" data-testid={`compare-${service.toLowerCase()}`}>
      <table className="w-full border-collapse min-w-[640px]">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-[#1A1612] text-left align-bottom p-4 w-36 min-w-[9rem]" />
            {packages.map((pkg) => (
              <th key={pkg.id} className="text-left align-bottom p-4 min-w-[13rem] border-b border-[#C5A880]/20" data-testid={`compare-col-${pkg.id}`}>
                <span className="text-[#EADBC8] font-semibold text-base block">{pkg.name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const Icon = row.icon;
            return (
              <tr key={row.key} className="border-b border-[#C5A880]/10 align-top">
                <th scope="row" className="sticky left-0 z-10 bg-[#1A1612] text-left p-4 text-[#EADBC8]/60 text-xs font-medium uppercase tracking-wide">
                  <span className="flex items-center gap-1.5">{Icon && <Icon size={13} className="text-[#C5A880]" />}{row.label}</span>
                </th>
                {packages.map((pkg) => (
                  <td key={pkg.id} className="p-4 text-[#EADBC8]/80 text-sm">{cellValue(pkg, row.key)}</td>
                ))}
              </tr>
            );
          })}
          <tr>
            <th className="sticky left-0 z-10 bg-[#1A1612] p-4" />
            {packages.map((pkg) => (
              <td key={pkg.id} className="p-4">
                <Link to="/quote"><Button size="sm" className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold" data-testid={`compare-quote-${pkg.id}`}>Request a quote</Button></Link>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
